import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  congesApi,
  type EpisodeArret,
  type JourDeRythme,
  type TravailDuJour,
} from '../../services/conges-api';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Case,
  Champ,
  Saisie,
  Section,
  Selection,
  ZoneTexte,
} from '../ndf/ui/Champ';
import { erreurGlobale, messageRefusPiece } from '../ndf/ui/erreurs-champs';
import { refusDuTypeDePiece } from '../../lib/piece-jointe';
import { aujourdhui } from '../../../server/lib/dates';
import { useToast } from '../../context/ToastContext';

/**
 * Arrêts de travail et mi-temps thérapeutique d'un salarié (specs §G).
 *
 * Posé sur la fiche salarié, où la RH travaille déjà : ouvrir un second écran
 * pour saisir un arrêt obligerait à chercher deux fois la même personne.
 *
 * TOUT ce qui est ici est réservé à la RH. Le manager n'atteint pas ces routes
 * (403), et les documents demandent en plus le droit « données médicales » :
 * saisir un arrêt et lire le certificat qui l'accompagne ne sont pas le même
 * geste (R-AT-04).
 */

// Réexportés depuis le socle commun : les appelants gardent leur adresse.
export { dateFr } from './arrets-commun';
import { dateFr } from './arrets-commun';
import { useTypesArret } from '../../hooks/useTypesArret';
import { DetailArret } from './DetailArret';

const JOURS: JourDeRythme['jour'][] = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi'];

const TRAVAIL: Array<{ valeur: TravailDuJour; libelle: string }> = [
  { valeur: 'journee', libelle: 'Journée' },
  { valeur: 'demi', libelle: 'Demi-journée' },
  { valeur: 'non', libelle: 'Non travaillé' },
];


function rythmeVide(): JourDeRythme[] {
  return JOURS.map((jour) => ({ jour, travail: 'journee', pauseCouverte: true }));
}

export function ArretsTravail({ salarieId }: { salarieId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [saisie, setSaisie] = useState<null | { arretInitialId: string | null }>(null);
  const [mtt, setMtt] = useState(false);
  const [detail, setDetail] = useState<string | null>(null);

  const arrets = useQuery({
    queryKey: ['conges', 'arrets', salarieId],
    queryFn: () => congesApi.absencesRh.arrets(salarieId),
    retry: false,
  });
  const miTemps = useQuery({
    queryKey: ['conges', 'mi-temps', salarieId],
    queryFn: () => congesApi.absencesRh.miTemps(salarieId),
    retry: false,
  });

  const rafraichir = () => {
    qc.invalidateQueries({ queryKey: ['conges', 'arrets', salarieId] });
    qc.invalidateQueries({ queryKey: ['conges', 'mi-temps', salarieId] });
    qc.invalidateQueries({ queryKey: ['conges', 'demandes'] });
  };

  const ouvrirDocument = async (pieceId: string) => {
    try {
      const p = await congesApi.absencesRh.document(pieceId);
      const binaire = atob(p.contenu_base64);
      const octets = new Uint8Array(binaire.length);
      for (let i = 0; i < binaire.length; i++) octets[i] = binaire.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([octets], { type: p.mime ?? 'application/pdf' }));
      window.open(url, '_blank');
    } catch (e) {
      toast.error(erreurGlobale(e) ?? 'Ce document ne vous est pas accessible.');
    }
  };

  // Une erreur 403 n'est pas une panne : c'est le fonctionnement attendu pour
  // qui n'a pas le droit de saisie RH. On n'affiche alors simplement rien.
  if (arrets.error) return null;

  const episodes = arrets.data?.episodes ?? [];
  // La date du jour se lit à PARIS, et non en temps universel : entre minuit et
  // deux heures du matin, un arrêt commencé le jour même ne se serait pas
  // affiché « en cours », et le 1er janvier à 00h30 l'année courante aurait été
  // la précédente — donc aucun épisode de l'année dans la liste des récents.
  const jour = aujourdhui();
  const anneeCourante = jour.slice(0, 4);
  const enCours = episodes.filter((e) => e.debut <= jour && e.fin >= jour);
  // On ne déplie que l'ANNÉE EN COURS : un salarié de dix ans d'ancienneté
  // aurait sinon trente épisodes à faire défiler pour atteindre le dernier.
  const recents = episodes.filter((e) => e.fin.slice(0, 4) >= anneeCourante);
  const anciens = episodes.length - recents.length;

  return (
    <Section
      titre="Arrêts de travail"
      teinte="corail"
      repliable
      ouvertParDefaut={false}
      // Le RÉSUMÉ porte ce qu'on vient chercher sans ouvrir : y a-t-il un arrêt
      // en cours, et combien d'épisodes au total.
      resume={
        episodes.length === 0
          ? 'Aucun arrêt'
          : `${episodes.length} épisode(s)${enCours.length ? `, ${enCours.length} en cours` : ''}`
      }
      description="Réservé à la RH. Le manager n’y a pas accès, et les documents demandent en plus le droit « données médicales »."
      actions={
        !saisie && !mtt ? (
          <div className="flex flex-wrap gap-2">
            <BoutonSecondaire type="button" onClick={() => setSaisie({ arretInitialId: null })}>
              Déclarer un arrêt
            </BoutonSecondaire>
            <BoutonSecondaire type="button" onClick={() => setMtt(true)}>
              Mi-temps thérapeutique
            </BoutonSecondaire>
          </div>
        ) : undefined
      }
    >
      {saisie && (
        <FormulaireArret
          salarieId={salarieId}
          arretInitialId={saisie.arretInitialId}
          episodes={episodes}
          onTermine={() => {
            setSaisie(null);
            rafraichir();
          }}
          onAnnuler={() => setSaisie(null)}
        />
      )}

      {mtt && (
        <FormulaireMiTemps
          salarieId={salarieId}
          episodes={episodes}
          onTermine={() => {
            setMtt(false);
            rafraichir();
          }}
          onAnnuler={() => setMtt(false)}
        />
      )}

      {!saisie && !mtt && (
        <>
          {episodes.length === 0 ? (
            <p className="text-sm text-nuit/60">Aucun arrêt enregistré.</p>
          ) : (
            <ul className="space-y-3">
              {recents.map((e) => (
                <li key={e.initial.id} className="rounded-lg border border-slate-200 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    {/* R-AT-06 : la ligne ouvre le détail, qui porte les
                        actions. Sans elle, modifier ou annuler un arrêt saisi
                        de travers était impossible. */}
                    <button
                      type="button"
                      className="flex-1 text-left"
                      onClick={() => setDetail(detail === e.initial.id ? null : e.initial.id)}
                    >
                      <p className="font-medium text-nuit">{e.initial.type_arret}</p>
                      {/* L'ÉPISODE, pas les arrêts : les lister à plat donnerait
                          cinq lignes de trois jours là où il y a un arrêt de
                          trois semaines (R-AT-02). */}
                      <p className="text-sm text-nuit/70">
                        Du {dateFr(e.debut)} au {dateFr(e.fin)}
                        {e.prolongations.length > 0 &&
                          ` (${e.prolongations.length} prolongation${e.prolongations.length > 1 ? 's' : ''})`}
                      </p>
                    </button>
                    <BoutonSecondaire
                      type="button"
                      onClick={() => setSaisie({ arretInitialId: e.initial.id })}
                    >
                      Prolonger
                    </BoutonSecondaire>
                  </div>

                  {detail === e.initial.id && (
                    <div className="mt-3">
                      <DetailArret arretId={e.initial.id} onFerme={() => setDetail(null)} />
                    </div>
                  )}

                  {e.prolongations.length > 0 && (
                    <ul className="mt-2 space-y-1 border-l-2 border-slate-200 pl-3 text-sm text-nuit/70">
                      <li>
                        Arrêt initial : du {dateFr(e.initial.date_debut)} au{' '}
                        {dateFr(e.initial.date_fin)}
                      </li>
                      {e.prolongations.map((p) => (
                        <li key={p.id}>
                          Prolongation : du {dateFr(p.date_debut)} au {dateFr(p.date_fin)}
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {e.attestations.map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => void ouvrirDocument(a.id)}
                        className="rounded bg-slate-100 px-2 py-1 text-xs text-nuit/80 hover:bg-slate-200"
                      >
                        {a.filename}
                      </button>
                    ))}
                    <AjoutAttestation arretId={e.initial.id} onFait={rafraichir} />
                  </div>
                </li>
              ))}
            </ul>
          )}

          {anciens > 0 && (
            <p className="mt-2 text-xs text-nuit/60">
              {anciens} épisode(s) des années précédentes, consultables depuis l’onglet « Arrêts de
              travail ».
            </p>
          )}

          {(miTemps.data?.periodes ?? []).length > 0 && (
            <div className="mt-4">
              <h3 className="mb-2 text-sm font-semibold text-nuit">Mi-temps thérapeutique</h3>
              <ul className="space-y-2">
                {(miTemps.data?.periodes ?? []).map((p) => (
                  <li key={p.id} className="rounded-lg border border-slate-200 p-3 text-sm">
                    <p className="font-medium text-nuit">
                      Du {dateFr(p.date_debut)} au {dateFr(p.date_fin)}
                    </p>
                    <p className="mt-1 text-nuit/70">
                      {p.rythme
                        .map(
                          (j) =>
                            `${j.jour} : ${
                              TRAVAIL.find((t) => t.valeur === j.travail)?.libelle ?? j.travail
                            }${j.travail !== 'non' && !j.pauseCouverte ? ' (pause non couverte)' : ''}`,
                        )
                        .join(', ')}
                    </p>
                    {p.commentaire && <p className="mt-1 text-nuit/60">{p.commentaire}</p>}
                    {p.exceptions.length > 0 && (
                      <ul className="mt-2 space-y-1 border-l-2 border-violet-fond pl-3 text-xs text-nuit/70">
                        {p.exceptions.map((x) => (
                          <li key={x.id}>
                            {dateFr(x.date)} : {TRAVAIL.find((t) => t.valeur === x.travail)?.libelle}
                            {x.motif ? ` (${x.motif})` : ''}
                          </li>
                        ))}
                      </ul>
                    )}
                    <AjoutException miTempsId={p.id} onFait={rafraichir} />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </Section>
  );
}

// ─── Déclarer un arrêt (R-AT-01) ────────────────────────────────────────────

export function FormulaireArret({
  salarieId,
  arretInitialId,
  episodes,
  onTermine,
  onAnnuler,
}: {
  salarieId: string;
  arretInitialId: string | null;
  episodes: EpisodeArret[];
  onTermine: () => void;
  onAnnuler: () => void;
}) {
  const toast = useToast();
  const initial = episodes.find((e) => e.initial.id === arretInitialId)?.initial ?? null;
  const { types: typesArret } = useTypesArret();
  const [type, setType] = useState(initial?.type_arret ?? '');
  const [debut, setDebut] = useState('');
  const [fin, setFin] = useState('');
  const [commentaire, setCommentaire] = useState('');
  const [piece, setPiece] = useState<{ filename: string; contenu_base64: string; mime: string } | null>(
    null,
  );

  const enregistrer = useMutation({
    mutationFn: (confirmeDuree: boolean) =>
      congesApi.absencesRh.declarer({
        salarie_id: salarieId,
        type_arret: type,
        date_debut: debut,
        date_fin: fin,
        arret_initial_id: arretInitialId,
        commentaire: commentaire || null,
        document: piece ?? undefined,
        confirme_duree: confirmeDuree,
      }),
    onSuccess: () => {
      toast.success(arretInitialId ? 'Prolongation enregistrée.' : 'Arrêt enregistré.');
      onTermine();
    },
    onError: (e: unknown) => {
      // R-AT-06 : au-delà d'un an, on confirme une fois. C'est presque toujours
      // une année mal recopiée, mais les invalidités longues existent : on
      // alerte, on n'interdit pas.
      const corps = (e as { body?: { confirmation_requise?: string; error?: string } })?.body;
      if (corps?.confirmation_requise === 'duree') {
        if (window.confirm(`${corps.error} Confirmer ces dates ?`)) enregistrer.mutate(true);
        return;
      }
      toast.error(messageRefusPiece(e) ?? erreurGlobale(e) ?? 'L’enregistrement a échoué.');
    },
  });

  // Pas de contrôle de taille ici : les pièces de la RH ne sont pas plafonnées
  // (décision du 07/10/2026). Un arrêt scanné par un tiers arrive à la
  // définition qu'il a, et la RH ne peut pas le refaire.
  const lire = (f: File, champ?: HTMLInputElement | null) => {
    // Le serveur refuse un fichier qui s'exécuterait à la réouverture ; le dire
    // ICI évite de lire un scan entier pour s'entendre refuser. Pas de contrôle
    // de TAILLE : les pièces de la RH ne sont pas plafonnées.
    const refus = refusDuTypeDePiece(f);
    if (refus) {
      toast.error(refus);
      // Le sélecteur est VIDÉ, comme le fait l'ajout d'attestation quinze
      // lignes plus bas : sans cela l'écran affiche le nom du fichier refusé
      // comme s'il était retenu, et une pièce valide choisie avant resterait
      // en place en silence (audit du 14/10/2026).
      if (champ) champ.value = '';
      return;
    }
    const l = new FileReader();
    l.onload = () => {
      const brut = String(l.result ?? '');
      setPiece({ filename: f.name, contenu_base64: brut.split(',')[1] ?? '', mime: f.type });
    };
    l.readAsDataURL(f);
  };

  return (
    <div className="space-y-3 rounded-lg border border-corail-fond/60 p-3">
      <p className="text-sm font-medium text-nuit">
        {arretInitialId
          ? `Prolongation de l’arrêt du ${dateFr(initial!.date_debut)}`
          : 'Nouvel arrêt de travail'}
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        <Champ label="Type d’arrêt" requis>
          {/* Une prolongation garde le type de son initial : changer de type en
              cours d'épisode décrirait un autre arrêt. */}
          <Selection value={type} onChange={(e) => setType(e.target.value)} disabled={Boolean(arretInitialId)}>
            {/* Le LIBELLÉ est la clé : c'est par lui que le serveur relie
                l'arrêt à son type d'absence. */}
            <option value="">Choisir</option>
            {typesArret.map((t) => (
              <option key={t.id} value={t.libelle}>
                {t.libelle}
              </option>
            ))}
          </Selection>
        </Champ>
        <Champ label="Document de l’arrêt" aide="PDF ou photo. Accessible aux seuls profils RH.">
          <input
            type="file"
            accept="image/*,application/pdf"
            className="block w-full text-sm text-nuit/70 file:mr-3 file:min-h-11 file:rounded-lg file:border-0 file:bg-ciel/30 file:px-4 file:text-sm file:font-medium file:text-nuit"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) lire(f, e.target);
            }}
          />
          {piece && <span className="mt-1 block text-xs text-nuit/60">{piece.filename}</span>}
        </Champ>
      </div>

      {/* « Du » et « Au » sont une SEULE information, une période : les séparer
          sur deux lignes obligeait à balayer l'écran pour la relire. */}
      <div className="grid grid-cols-2 gap-3 sm:max-w-md">
        <Champ label="Du" requis>
          <Saisie type="date" value={debut} onChange={(e) => setDebut(e.target.value)} />
        </Champ>
        <Champ label="Au" requis>
          <Saisie type="date" value={fin} min={debut} onChange={(e) => setFin(e.target.value)} />
        </Champ>
      </div>

      <Champ label="Commentaire">
        <ZoneTexte value={commentaire} onChange={(e) => setCommentaire(e.target.value)} rows={3} />
      </Champ>

      <div className="flex flex-wrap items-center justify-end gap-2">
        {(!debut || !fin) && (
          <p className="mr-auto text-sm text-corail">Indiquez la période de l’arrêt.</p>
        )}
        <BoutonSecondaire type="button" onClick={onAnnuler}>
          Annuler
        </BoutonSecondaire>
        <BoutonPrincipal
          type="button"
          disabled={!debut || !fin || enregistrer.isPending}
          onClick={() => enregistrer.mutate(false)}
        >
          {enregistrer.isPending ? 'Enregistrement…' : 'Enregistrer'}
        </BoutonPrincipal>
      </div>
    </div>
  );
}

// ─── Attestations de salaire (R-AT-03) ──────────────────────────────────────

export function AjoutAttestation({ arretId, onFait }: { arretId: string; onFait: () => void }) {
  const toast = useToast();
  const envoyer = useMutation({
    mutationFn: (p: { filename: string; contenu_base64: string; mime: string }) =>
      congesApi.absencesRh.attestation(arretId, p),
    onSuccess: () => {
      toast.success('Attestation rattachée.');
      onFait();
    },
    onError: (e) => toast.error(messageRefusPiece(e) ?? erreurGlobale(e) ?? 'Le dépôt a échoué.'),
  });

  return (
    <label className="cursor-pointer text-xs text-violet underline">
      Ajouter une attestation
      <input
        type="file"
        accept="application/pdf,image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          const refus = refusDuTypeDePiece(f);
          if (refus) {
            toast.error(refus);
            e.target.value = '';
            return;
          }
          const l = new FileReader();
          l.onload = () =>
            envoyer.mutate({
              filename: f.name,
              contenu_base64: String(l.result ?? '').split(',')[1] ?? '',
              mime: f.type,
            });
          l.readAsDataURL(f);
        }}
      />
    </label>
  );
}

// ─── Mi-temps thérapeutique (R-MTT-01) ──────────────────────────────────────

export function FormulaireMiTemps({
  salarieId,
  episodes,
  onTermine,
  onAnnuler,
}: {
  salarieId: string;
  episodes: EpisodeArret[];
  onTermine: () => void;
  onAnnuler: () => void;
}) {
  const toast = useToast();
  const [debut, setDebut] = useState('');
  const [fin, setFin] = useState('');
  const [arretId, setArretId] = useState('');
  const [commentaire, setCommentaire] = useState('');
  const [rythme, setRythme] = useState<JourDeRythme[]>(rythmeVide);

  const majJour = (jour: JourDeRythme['jour'], modif: Partial<JourDeRythme>) =>
    setRythme((r) => r.map((j) => (j.jour === jour ? { ...j, ...modif } : j)));

  const enregistrer = useMutation({
    mutationFn: () =>
      congesApi.absencesRh.creerMiTemps({
        salarie_id: salarieId,
        arret_travail_id: arretId || null,
        date_debut: debut,
        date_fin: fin,
        rythme,
        commentaire: commentaire || null,
      }),
    onSuccess: () => {
      toast.success('Mi-temps thérapeutique enregistré.');
      onTermine();
    },
    onError: (e) => toast.error(erreurGlobale(e) ?? 'L’enregistrement a échoué.'),
  });

  return (
    <div className="space-y-3 rounded-lg border border-violet-fond/60 p-3">
      <p className="text-sm font-medium text-nuit">Mi-temps thérapeutique</p>

      <div className="grid gap-3 sm:grid-cols-3">
        <Champ label="Du" requis>
          <Saisie type="date" value={debut} onChange={(e) => setDebut(e.target.value)} />
        </Champ>
        <Champ label="Au" requis>
          <Saisie type="date" value={fin} min={debut} onChange={(e) => setFin(e.target.value)} />
        </Champ>
        <Champ label="Arrêt associé">
          <Selection value={arretId} onChange={(e) => setArretId(e.target.value)}>
            <option value="">Aucun</option>
            {episodes.map((e) => (
              <option key={e.initial.id} value={e.initial.id}>
                {e.initial.type_arret}, du {dateFr(e.debut)}
              </option>
            ))}
          </Selection>
        </Champ>
      </div>

      <div>
        <p className="mb-2 text-sm font-medium text-nuit">Rythme hebdomadaire</p>
        <div className="space-y-2">
          {rythme.map((j) => (
            <div key={j.jour} className="flex flex-wrap items-center gap-3">
              <span className="w-24 text-sm capitalize text-nuit/80">{j.jour}</span>
              <Selection
                className="w-auto min-w-40"
                value={j.travail}
                onChange={(e) => majJour(j.jour, { travail: e.target.value as TravailDuJour })}
              >
                {TRAVAIL.map((t) => (
                  <option key={t.valeur} value={t.valeur}>
                    {t.libelle}
                  </option>
                ))}
              </Selection>
              {/* La pause n'a de sens que sur un jour travaillé : elle pilote le
                  droit au ticket restaurant (R-TKT-06). */}
              {j.travail !== 'non' && (
                <Case
                  label="Pause déjeuner couverte"
                  checked={j.pauseCouverte}
                  onChange={(e) => majJour(j.jour, { pauseCouverte: e.target.checked })}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      <Champ label="Commentaire" aide="Les règles propres au cas, que le rythme ne dit pas.">
        <ZoneTexte value={commentaire} onChange={(e) => setCommentaire(e.target.value)} rows={3} />
      </Champ>

      <div className="flex flex-wrap items-center justify-end gap-2">
        {(!debut || !fin) && <p className="mr-auto text-sm text-corail">Indiquez la période.</p>}
        <BoutonSecondaire type="button" onClick={onAnnuler}>
          Annuler
        </BoutonSecondaire>
        <BoutonPrincipal
          type="button"
          disabled={!debut || !fin || enregistrer.isPending}
          onClick={() => enregistrer.mutate()}
        >
          {enregistrer.isPending ? 'Enregistrement…' : 'Enregistrer'}
        </BoutonPrincipal>
      </div>
    </div>
  );
}

// ─── Exceptions ponctuelles (R-MTT-02) ──────────────────────────────────────

export function AjoutException({ miTempsId, onFait }: { miTempsId: string; onFait: () => void }) {
  const toast = useToast();
  const [ouvert, setOuvert] = useState(false);
  const [date, setDate] = useState('');
  const [travail, setTravail] = useState<TravailDuJour>('journee');
  const [pause, setPause] = useState(true);
  const [motif, setMotif] = useState('');

  const poser = useMutation({
    mutationFn: () =>
      congesApi.absencesRh.exception(miTempsId, {
        date,
        travail,
        pause_couverte: pause,
        motif: motif || null,
      }),
    onSuccess: () => {
      toast.success('Exception enregistrée.');
      setOuvert(false);
      setDate('');
      setMotif('');
      onFait();
    },
    onError: (e) => toast.error(erreurGlobale(e) ?? 'L’enregistrement a échoué.'),
  });

  if (!ouvert) {
    return (
      <button
        type="button"
        onClick={() => setOuvert(true)}
        className="mt-2 text-xs text-violet underline"
      >
        Ajouter une exception
      </button>
    );
  }

  return (
    <div className="mt-2 flex flex-wrap items-end gap-2 rounded border border-violet-fond/60 p-2">
      <Champ label="Date" requis>
        <Saisie type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </Champ>
      <Champ label="Ce jour-là">
        <Selection value={travail} onChange={(e) => setTravail(e.target.value as TravailDuJour)}>
          {TRAVAIL.map((t) => (
            <option key={t.valeur} value={t.valeur}>
              {t.libelle}
            </option>
          ))}
        </Selection>
      </Champ>
      {travail !== 'non' && (
        <Case label="Pause couverte" checked={pause} onChange={(e) => setPause(e.target.checked)} />
      )}
      <Champ label="Motif">
        <Saisie value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Semaine alternée" />
      </Champ>
      <BoutonSecondaire type="button" onClick={() => setOuvert(false)}>
        Annuler
      </BoutonSecondaire>
      <BoutonPrincipal type="button" disabled={!date || poser.isPending} onClick={() => poser.mutate()}>
        Enregistrer
      </BoutonPrincipal>
    </div>
  );
}
